import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { convertMillisToHoursMinutes } from 'src/app/@core/utils/convertMillisToHoursMinutes.functions';
import {
  DatatableColumn,
  DatatableColumnType,
  SearchControl,
  SearchControlType,
  StringMap,
} from 'src/app/@theme/datatable/datatable.component';

export interface ChampionTvRow {
  month: string;
  room: string;
  level: string;
  easy: { picture: string; teamName: string };
  medium: { picture: string; teamName: string };
  hard: { picture: string; teamName: string };
  master: { picture: string; teamName: string };
}

@Component({
  selector: 'app-championtv',
  templateUrl: './championtv.component.html',
  styleUrls: ['./championtv.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChampionTvComponent {
  championsTv: any[] = [];
  championTv: ChampionTvRow[] = [];
  searchControlSettings: SearchControl[] = [];
  searchable: boolean = false;
  month: string = '';
  columns: DatatableColumn[] = [];

  @Input() loading: boolean = false;
  @Input() theme: string = '';
  @Input() minHeight: string = '700px';

  @Input('month') set setMonth(month: string) {
    this.month = month;
    this.mapBranchChampionRows();
  }

  @Input('championsTv') set setChampionsTv(championsTv: any[]) {
    this.championsTv = championsTv;
    this.buildSearchControlSettings();
    this.mapBranchChampionRows();
  }

  @Output() filterChange = new EventEmitter<any>();

  @HostBinding('class.light-theme') get isLightTheme() {
    return this.theme === 'light';
  }
  @HostBinding('class.dark-theme') get isDarkTheme() {
    return this.theme === 'dark';
  }

  private buildSearchControlSettings(): void {
    if (!this.championTv) {
      return;
    }

    const today = new Date();
    const months: StringMap = {};
    const originalToday = new Date(today);

    for (let i = 0; i < 4; i++) {
      const dateKey =
        today.getFullYear() +
        '.' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '.';
      const dateValue = today.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
      });

      months[dateKey] = dateValue;
      today.setMonth(today.getMonth() - 1);
    }

    const defaultMonth =
      originalToday.getFullYear() +
      '.' +
      String(originalToday.getMonth() + 1).padStart(2, '0') +
      '.';

    this.searchControlSettings = [
      {
        title: 'Month',
        type: SearchControlType.SELECT,
        property: 'month',
        options: months,
        defaultValue: defaultMonth,
      },
    ];
  }

  private mapBranchChampionRows(): void {
    if (this.championsTv) {
      const rows: ChampionTvRow[] = [];
      const roomNames: string[] = [];
      const levels: string[] = ['x_hard', 'hard', 'medium', 'easy'];

      this.championsTv.forEach((champion) => {
        champion.data!.forEach((entry: { room: string }) => {
          if (!roomNames.includes(entry.room)) {
            roomNames.push(entry.room);
          }
        });
      });

      this.columns = [
        { prop: 'level', name: '', sortable: false, width: 80 },
        ...roomNames
          .sort((a, b) => (a < b ? -1 : 1))
          .map((room) => ({
            prop: room,
            name: room.charAt(0).toUpperCase() + room.slice(1),
            width: 125,
            sortable: false,
            type: DatatableColumnType.ARRAY,
          })),
      ];

      levels.forEach((level) => {
        const roomChampionRow: ChampionTvRow = {
          month: this.month,
          room:
            level === 'x_hard'
              ? 'Master'
              : level.charAt(0).toUpperCase() + level.slice(1),
          level:
            level === 'x_hard'
              ? 'Master'
              : level.charAt(0).toUpperCase() + level.slice(1),

          easy: { picture: '', teamName: '' },
          medium: { picture: '', teamName: '' },
          hard: { picture: '', teamName: '' },
          master: { picture: '', teamName: '' },
        };

        roomNames.forEach((room) => {
          const champion = this.championsTv.find((champion) => {
            return champion.data!.some(
              (entry: { room: string; level: string }) =>
                entry.room === room && entry.level.toLowerCase() === level,
            );
          });
          if (champion) {
            const entry = champion.data!.find(
              (entry: { room: string; level: string }) =>
                entry.room === room && entry.level.toLowerCase() === level,
            );
            const timeInHoursAndMinutes = convertMillisToHoursMinutes(
              entry.time,
            );
            const picture = entry.picture
              ? entry.picture
              : '../../../../assets/noteampicture.png';
            const teamName = entry.teamName;
            (roomChampionRow as any)[room] = {
              picture,
              teamName: timeInHoursAndMinutes + ' - ' + teamName,
              month: entry.month,
            };
          } else {
            (roomChampionRow as any)[room] = {
              month: '',
              picture: '',
              teamName: '',
            };
          }
        });
        rows.push(roomChampionRow);
      });

      this.championTv = rows;
    }
  }

  onFilterChange(event: any): void {
    this.month = event['month'];
    this.filterChange.emit(event);
  }
}
